import { useEffect, useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import LanguageOutlinedIcon from '@mui/icons-material/LanguageOutlined';
import LoginOutlinedIcon from '@mui/icons-material/LoginOutlined';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import { loginWithGoogle } from '../../api/authApi';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import HubOutlinedIcon from '@mui/icons-material/HubOutlined';
import { useAuth } from '../../context/AuthContext';
import { useBackendStatus } from '../../hooks/useBackendApi';
import { useTheme } from '../../hooks/useTheme';
import './LoginPage.css';

type Mode = 'login' | 'register';

interface AppTheme {
  name?: string;
  background: string;
  toolbar: { buttonBackground: string; buttonBorder: string; buttonText: string };
}

interface ThemeContextValue {
  theme: AppTheme;
  toggleTheme?: () => void;
}

const deriveUsername = (email: string) => {
  const localPart = email.trim().split('@')[0] || email.trim();
  return localPart.toLowerCase().replace(/[^a-z0-9._-]/g, '.').replace(/\.+/g, '.').replace(/^\.|\.$/g, '') || 'user';
};

const isSimpleEmailValid = (email: string) => email.trim().includes('@');
const isStrongRegistrationPassword = (value: string) =>
  value.length >= 8
  && /[A-Za-zА-Яа-я]/.test(value)
  && /\d/.test(value)
  && /[^A-Za-zА-Яа-я0-9]/.test(value);

const platformSlides = [
  {
    icon: <AccountTreeOutlinedIcon fontSize="small" />,
    kicker: 'BCM',
    title: 'Карта бизнес-способностей',
    description: 'Показывает бизнес-способности по уровням, связи с системами и кастомные карты для отдельных сценариев.',
    metric: 'Default + custom',
    preview: ['Стратегия', 'Продукты', 'Операции', 'Платформа'],
  },
  {
    icon: <HubOutlinedIcon fontSize="small" />,
    kicker: 'Registry',
    title: 'Реестры и связи',
    description: 'Единое место для бизнес-объектов, информационных систем, кастомных объектов и наследуемых отношений.',
    metric: 'Связанные записи',
    preview: ['BC', 'System', 'Owner', 'Relation'],
  },
  {
    icon: <AccountTreeOutlinedIcon fontSize="small" />,
    kicker: 'Boards',
    title: 'Диаграммы и доски',
    description: 'C4, BPMN, ArchiMate, ERD, UML и свободные схемы в одном пространстве.',
    metric: 'C4 / BPMN / ERD / UML',
    preview: ['C4', 'BPMN', 'ERD', 'UML'],
  },
  {
    icon: <ShieldOutlinedIcon fontSize="small" />,
    kicker: 'Operations',
    title: 'Контроль платформы',
    description: 'Карты здоровья, Kubernetes discovery, GitLab-синхронизация и администрирование доступа.',
    metric: 'Health + sync',
    preview: ['Health', 'K8s', 'GitLab', 'Access'],
  },
];

export default function LoginPage() {
  const { login, register, isAuthenticated, isLoading: authLoading } = useAuth();
  const { usingBackend, checking } = useBackendStatus();
  const { theme, toggleTheme } = useTheme() as ThemeContextValue;
  const navigate = useNavigate();
  const location = useLocation();
  const pathname = location.pathname;
  const search = location.search;

  const initialMode: Mode = pathname === '/register' ? 'register' : 'login';
  const [mode, setMode] = useState<Mode>(initialMode);
  const [loginEmail, setLoginEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [workEmail, setWorkEmail] = useState('');
  const [verificationEmail, setVerificationEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [activeSlide, setActiveSlide] = useState(0);

  const from = (location.state as { from?: { pathname?: string } })?.from?.pathname ?? '/';
  const isRegister = mode === 'register';
  const isDark = theme.name === 'dark';
  const emailVerified = useMemo(() => new URLSearchParams(search).get('email_verified') === '1', [search]);

  useEffect(() => {
    const nextMode: Mode = pathname === '/register' ? 'register' : 'login';
    setMode(nextMode);
    setError('');
    setVerificationEmail('');
  }, [pathname]);

  const inferredUsername = useMemo(() => deriveUsername(workEmail), [workEmail]);
  const currentSlide = platformSlides[activeSlide];
  const logoSrc = `${process.env.PUBLIC_URL || ''}/logo_base.jpg`;

  if (checking || authLoading) return null;
  if (!usingBackend) return <Navigate to="/" replace />;
  if (isAuthenticated) return <Navigate to={from} replace />;

  const switchMode = (nextMode: Mode) => {
    setMode(nextMode);
    setError('');
    setNotice('');
    setVerificationEmail('');
    navigate(nextMode === 'register' ? '/register' : '/login', { replace: false, state: location.state });
  };

  const showSlide = (direction: -1 | 1) => {
    setActiveSlide((prev) => (prev + direction + platformSlides.length) % platformSlides.length);
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError('');
    setNotice('');

    if (isRegister && !isSimpleEmailValid(workEmail)) {
      setError('Email должен содержать @');
      return;
    }

    if (isRegister && !isStrongRegistrationPassword(password)) {
      setError('Пароль должен быть не короче 8 символов и содержать букву, цифру и спецсимвол');
      return;
    }

    if (isRegister && password !== confirmPassword) {
      setError('Пароли не совпадают');
      return;
    }

    if (isRegister && !acceptedTerms) {
      setError('Подтвердите условия использования');
      return;
    }

    setSubmitting(true);
    try {
      if (!isRegister) {
        await login(loginEmail.trim(), password);
        navigate(from, { replace: true });
        return;
      }

      const user = await register(inferredUsername, workEmail.trim(), password, displayName.trim() || undefined);
      if (user) {
        navigate(from, { replace: true });
      } else {
        setVerificationEmail(workEmail.trim());
        setPassword('');
        setConfirmPassword('');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ошибка авторизации');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className={`auth-page ${isDark ? 'auth-page--dark' : ''}`}>
      <header className="auth-topbar" aria-label="Панель авторизации">
        <div className="auth-brand">
          <img className="auth-brand__logo" src={logoSrc} alt="" aria-hidden="true" />
          <span>Aspect</span>
        </div>
        <div className="auth-topbar__actions">
          <button type="button" className="auth-icon-button" title="Язык интерфейса">
            <LanguageOutlinedIcon fontSize="small" />
            <span>RU</span>
          </button>
          <button type="button" className="auth-icon-button" title="Переключить тему" onClick={toggleTheme}>
            <DarkModeOutlinedIcon fontSize="small" />
          </button>
        </div>
      </header>

      <section className="auth-layout" aria-label="Aspect авторизация">
        <aside className="auth-context" aria-label="Контекст продукта">
          <p className="auth-eyebrow">Enterprise architecture workspace</p>
          <h1 className="auth-title">
            <img src={logoSrc} alt="" aria-hidden="true" />
            <span>Aspect</span>
          </h1>
          <p className="auth-context__lead">
            Arch as Code платформа, ускоряющая работу архитектора в разы
          </p>

          <div className="auth-platform-slider" aria-label="Основные функции платформы">
            <div className="auth-platform-slider__header">
              <div>
                <p>Основные функции платформы</p>
                <h2>{currentSlide.title}</h2>
              </div>
              <div className="auth-platform-slider__actions">
                <button type="button" aria-label="Предыдущая функция" onClick={() => showSlide(-1)}>
                  ←
                </button>
                <button type="button" aria-label="Следующая функция" onClick={() => showSlide(1)}>
                  →
                </button>
              </div>
            </div>
            <div className="auth-platform-slider__body">
              <div className="auth-slide-copy">
                <span className="auth-slide-kicker">
                  {currentSlide.icon}
                  {currentSlide.kicker}
                </span>
                <p>{currentSlide.description}</p>
                <strong>{currentSlide.metric}</strong>
              </div>
              <div className="auth-slide-preview" aria-label={`${currentSlide.title}: превью`}>
                {currentSlide.preview.map((item, index) => (
                  <div className={index === 0 ? 'auth-slide-node auth-slide-node--primary' : 'auth-slide-node'} key={item}>
                    {item}
                  </div>
                ))}
              </div>
            </div>
            <div className="auth-slider-dots" aria-label="Навигация по функциям">
              {platformSlides.map((slide, index) => (
                <button
                  key={slide.title}
                  type="button"
                  aria-label={`Показать: ${slide.title}`}
                  aria-current={activeSlide === index}
                  onClick={() => setActiveSlide(index)}
                />
              ))}
            </div>
          </div>
        </aside>

        <section className="auth-panel" aria-label={isRegister ? 'Регистрация' : 'Вход'}>
          <div className="auth-segmented" aria-label="Режим авторизации">
            <button type="button" aria-pressed={!isRegister} onClick={() => switchMode('login')}>
              Вход
            </button>
            <button type="button" aria-pressed={isRegister} onClick={() => switchMode('register')}>
              Регистрация
            </button>
          </div>

          <div className="auth-panel__heading">
            <h2>{isRegister ? 'Создать аккаунт' : 'Войти в рабочее пространство'}</h2>
            <p>{isRegister ? 'Укажите Username, рабочую почту и пароль.' : 'Введите рабочую почту и пароль.'}</p>
          </div>

          {emailVerified && !isRegister && (
            <output className="auth-status auth-status--success">
              <strong>Почта подтверждена</strong>
              <span>Войдите, чтобы перейти к работе на платформе.</span>
            </output>
          )}

          {isRegister && verificationEmail ? (
            <output className="auth-registration-success">
              <div className="auth-registration-success__mark">✓</div>
              <div>
                <p className="auth-registration-success__eyebrow">Письмо отправлено</p>
                <h3>Подтвердите почту</h3>
                <p>
                  Мы отправили ссылку подтверждения на <strong>{verificationEmail}</strong>.
                  Проверьте папку Спам, если письмо не появилось во входящих в течение нескольких минут.
                </p>
              </div>
              <div className="auth-registration-success__actions">
                <button
                  type="button"
                  className="auth-submit"
                  onClick={() => {
                    const email = verificationEmail;
                    switchMode('login');
                    setLoginEmail(email);
                  }}
                >
                  Перейти ко входу
                </button>
                <button
                  type="button"
                  className="auth-link-button"
                  onClick={() => {
                    setVerificationEmail('');
                    setPassword('');
                    setConfirmPassword('');
                  }}
                >
                  Использовать другой email
                </button>
              </div>
            </output>
          ) : (
          <form className="auth-form" noValidate onSubmit={(event) => void handleSubmit(event)}>
            {isRegister ? (
              <>
                <label>
                  <span>Username</span>
                  <input
                    type="text"
                    value={displayName}
                    onChange={(event) => setDisplayName(event.target.value)}
                    autoComplete="name"
                    required
                  />
                </label>
                <label>
                  <span>Рабочая почта</span>
                  <input
                    type="email"
                    value={workEmail}
                    onChange={(event) => setWorkEmail(event.target.value)}
                    autoComplete="email"
                    required
                  />
                </label>
                <div className="auth-form__grid">
                  <label>
                    <span>Пароль</span>
                    <input
                      type="password"
                      value={password}
                      onChange={(event) => setPassword(event.target.value)}
                      autoComplete="new-password"
                      required
                    />
                  </label>
                  <label>
                    <span>Повторите пароль</span>
                    <input
                      type="password"
                      value={confirmPassword}
                      onChange={(event) => setConfirmPassword(event.target.value)}
                      autoComplete="new-password"
                      required
                    />
                  </label>
                </div>
                <label className="auth-checkbox">
                  <input
                    type="checkbox"
                    checked={acceptedTerms}
                    onChange={(event) => setAcceptedTerms(event.target.checked)}
                  />
                  <span>Принимаю условия использования и политики безопасности</span>
                </label>
              </>
            ) : (
              <>
                <label>
                  <span>Email</span>
                  <input
                    type="email"
                    value={loginEmail}
                    onChange={(event) => setLoginEmail(event.target.value)}
                    autoComplete="username"
                    required
                  />
                </label>
                <label>
                  <span>Password</span>
                  <input
                    type="password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    autoComplete="current-password"
                    required
                  />
                </label>
                <div className="auth-form__meta">
                  <button type="button" className="auth-link-button">
                    Забыли пароль?
                  </button>
                </div>
              </>
            )}

            {error && (
              <p role="alert" className="auth-error">
                {error}
              </p>
            )}
            {notice && (
              <p role="alert" className="auth-error">
                {notice}
              </p>
            )}

            <button type="submit" className="auth-submit" disabled={submitting}>
              <LoginOutlinedIcon fontSize="small" />
              {submitting ? '...' : isRegister ? 'Создать аккаунт' : 'Войти'}
            </button>

            {!isRegister && (
              <button type="button" className="auth-sso" onClick={loginWithGoogle}>
                <span className="auth-sso__mark">G</span>
                Войти через Google
              </button>
            )}
          </form>
          )}

          <footer className="auth-footer">
            <span>Version 0.0.2</span>
          </footer>
        </section>
      </section>
    </main>
  );
}
